import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Palette, 
  Loader2,
  Image,
  MessageSquare,
  Store
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const presetColors = [
  { name: 'Teal', value: '#5DBDB3' },
  { name: 'Coral', value: '#F08B7D' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Pink', value: '#EC4899' },
];

const StoreBrandingDialog = ({ store, token, open, onOpenChange, onUpdate }) => {
  const [formData, setFormData] = useState({
    logo_url: '',
    primary_color: '#5DBDB3',
    secondary_color: '#F08B7D',
    welcome_message: ''
  });
  const [saving, setSaving] = useState(false);
  const [previewLogo, setPreviewLogo] = useState(null);

  useEffect(() => {
    if (store) {
      setFormData({
        logo_url: store.logo_url || '',
        primary_color: store.primary_color || '#5DBDB3',
        secondary_color: store.secondary_color || '#F08B7D',
        welcome_message: store.welcome_message || ''
      });
      setPreviewLogo(store.logo_url || null);
    }
  }, [store]);

  const handleLogoChange = (e) => {
    const url = e.target.value;
    setFormData({ ...formData, logo_url: url });
    if (url) {
      setPreviewLogo(url);
    } else {
      setPreviewLogo(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/api/stores/${store.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Store branding updated!');
      onUpdate?.(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#5DBDB3]" />
            Customize Store Branding
          </DialogTitle>
          <DialogDescription>
            Personalize how your store appears on the QR scan page
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview */}
          <div className="p-4 border rounded-lg">
            <p className="text-xs text-muted-foreground mb-2">Preview</p>
            <div 
              className="h-2 rounded-t"
              style={{ background: `linear-gradient(to right, ${formData.primary_color}, ${formData.secondary_color})` }}
            />
            <div className="p-4 bg-muted rounded-b flex items-center gap-3">
              {previewLogo ? (
                <img 
                  src={previewLogo} 
                  alt="Store logo" 
                  className="w-12 h-12 rounded-lg object-contain bg-white"
                  onError={() => setPreviewLogo(null)}
                />
              ) : (
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${formData.primary_color}20` }}
                >
                  <Store className="w-6 h-6" style={{ color: formData.primary_color }} />
                </div>
              )}
              <div>
                <p className="font-medium text-sm">{store?.name}</p>
                {formData.welcome_message && (
                  <p 
                    className="text-xs mt-1 px-2 py-1 rounded"
                    style={{ backgroundColor: `${formData.primary_color}15`, color: formData.primary_color }}
                  >
                    {formData.welcome_message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Logo URL */}
          <div className="space-y-2">
            <Label htmlFor="logo_url" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Logo URL
            </Label>
            <Input
              id="logo_url"
              placeholder="https://example.com/your-logo.png"
              value={formData.logo_url}
              onChange={handleLogoChange}
            />
            <p className="text-xs text-muted-foreground">
              Enter a URL to your store logo (recommended: square image, min 100x100px)
            </p>
          </div>

          {/* Primary Color */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Primary Color
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="color"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                placeholder="#5DBDB3"
                className="flex-1"
              />
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {presetColors.map(color => (
                <button
                  key={color.value}
                  type="button"
                  className="w-6 h-6 rounded-full border-2 border-transparent hover:border-foreground transition-all"
                  style={{ backgroundColor: color.value }}
                  onClick={() => setFormData({ ...formData, primary_color: color.value })}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {/* Secondary Color */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Secondary Color
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="color"
                value={formData.secondary_color}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={formData.secondary_color}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                placeholder="#F08B7D"
                className="flex-1"
              />
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {presetColors.map(color => (
                <button
                  key={color.value}
                  type="button"
                  className="w-6 h-6 rounded-full border-2 border-transparent hover:border-foreground transition-all"
                  style={{ backgroundColor: color.value }}
                  onClick={() => setFormData({ ...formData, secondary_color: color.value })}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {/* Welcome Message */}
          <div className="space-y-2">
            <Label htmlFor="welcome_message" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Welcome Message
            </Label>
            <Textarea
              id="welcome_message"
              placeholder="Welcome to our store! We're here to help you find allergen-safe products."
              value={formData.welcome_message}
              onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              A short message displayed to customers when they scan your QR code
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#5DBDB3] hover:bg-[#4AA89E]"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Palette className="w-4 h-4 mr-2" />}
            Save Branding
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StoreBrandingDialog;
